/**
 * Organization membership, derived from the signed-in user's Cognito groups.
 *
 * Group naming (see amplify/function/autherodePreTokenGeneration/src/custom.js and
 * docs/architecture/ORGANIZATION_ACCESS.md):
 *   - "Admin" and "AdminReadOnly" are platform role groups.
 *   - "<userPoolId>_Google" is auto-assigned to every Google-federated user; it is not an org.
 *   - Every other group name is an Organization id.
 *
 * This is the one place that reads meaning into a group name. VerticalNav's /home → AI Analyst
 * redirect, the nav's Admin and Tools gating, and the Tools pages all go through these helpers.
 */
export type CognitoGroups = ReadonlyArray<string> | null | undefined;

export const isGoogleIdpGroup = (group: string): boolean => group.includes("Google");

export const isAdminGroup = (group: string): boolean => group.includes("Admin");

/** Every group except the auto-generated Google IdP group. Admin groups are kept: they are real groups. */
export const getRealGroups = (groups: CognitoGroups): string[] =>
  (groups ?? []).filter((group) => !isGoogleIdpGroup(group));

/** The organizations the user belongs to, as Organization ids. */
export const getOrganizationIds = (groups: CognitoGroups): string[] =>
  getRealGroups(groups).filter((group) => !isAdminGroup(group));

/** In an organization or an admin — the users who stay on /home and see the Tools entry. */
export const hasOrganizationAccess = (groups: CognitoGroups): boolean =>
  getRealGroups(groups).length > 0;

/** Admin or AdminReadOnly — what the nav's "admin" visibility means. */
export const isAdminUser = (groups: CognitoGroups): boolean =>
  (groups ?? []).includes("Admin") || (groups ?? []).includes("AdminReadOnly");

/** Write-capable admin. AdminReadOnly is excluded: its AppSync writes are rejected. */
export const isFullAdmin = (groups: CognitoGroups): boolean => (groups ?? []).includes("Admin");
